import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { MoreVertical } from 'lucide-react';
import { Button } from './button.js';
import { DropdownMenu } from './dropdown-menu.js';
const RowActionsMenuContext = React.createContext(null);
/**
 * Row «⋮» menu.
 *
 * Every action in these menus that opens a dialog (`ConfirmDialog`,
 * `Dialog`) renders it from state OUTSIDE the menu. That has a focus trap
 * of its own: Radix dispatches `onSelect` inside `flushSync`, so a dialog
 * opened straight from the handler mounts BEFORE the menu closes and records
 * the menu item — about to be unmounted — as the element to return focus to.
 * The menu's own restore-to-trigger runs later (`setTimeout` in Radix's
 * FocusScope) and is bounced by the now-open dialog, so after «Отмена» the
 * focus fell on `<body>`.
 *
 * `select` therefore closes the menu (controlled `open`) and runs the action
 * from the effect that observes `open === false`: React has already run the
 * unmounted menu's cleanups (its focus-trap listeners are gone), so focusing
 * ⋮ sticks, and the dialog the action opens records ⋮ as the place to come
 * back to. An effect, not a microtask/timeout: React commits the close in a
 * microtask of its own, so any hand-scheduled deferral is a race with it.
 * `RowActionsMenu.Item` / `.DangerItem` take this path; a plain
 * `DropdownMenu.Item` still works for actions that open nothing (navigation).
 */
export function RowActionsMenu({ 'aria-label': ariaLabel, children }) {
    const triggerRef = React.useRef(null);
    const [open, setOpen] = React.useState(false);
    const pending = React.useRef(null);
    // Set by `select`, cleared when the menu content has unmounted. While set,
    // the menu's own deferred restore-to-trigger (Radix runs it in a
    // `setTimeout` after unmount) is suppressed: ⋮ was focused here already,
    // and by then the action's dialog is open — the late `focus()` would pull
    // focus OUT of it (its trap has nothing to bounce back to yet when the
    // dialog's first field took focus via `autoFocus` during commit).
    const closedBySelect = React.useRef(false);
    React.useEffect(() => {
        if (open || !pending.current)
            return;
        const action = pending.current;
        pending.current = null;
        triggerRef.current?.focus();
        action();
    }, [open]);
    const ctx = React.useMemo(() => ({
        select: (action) => {
            pending.current = action;
            closedBySelect.current = true;
            setOpen(false);
        },
    }), []);
    const onCloseAutoFocus = React.useCallback((event) => {
        if (!closedBySelect.current)
            return;
        closedBySelect.current = false;
        event.preventDefault();
    }, []);
    return (_jsx(RowActionsMenuContext.Provider, { value: ctx, children: _jsxs(DropdownMenu.Root, { open: open, onOpenChange: setOpen, children: [_jsx(DropdownMenu.Trigger, { asChild: true, children: _jsx(Button, { ref: triggerRef, size: "sm", variant: "ghost", "aria-label": ariaLabel, className: "shrink-0", children: _jsx(MoreVertical, { className: "h-4 w-4" }) }) }), _jsx(DropdownMenu.Content, { align: "end", onCloseAutoFocus: onCloseAutoFocus, children: children })] }) }));
}
/** Outside a `RowActionsMenu` (tests, ad-hoc menus) the action just runs. */
function useSelect() {
    const ctx = React.useContext(RowActionsMenuContext);
    return ctx ? ctx.select : (action) => action();
}
/**
 * Ordinary item whose action opens a dialog. The menu closes on select like
 * any other item; the action runs once it is gone (see `RowActionsMenu`).
 * NO `preventDefault` — that is only for a dialog rendered INSIDE the menu,
 * and holding the menu open left it showing through under the overlay and
 * still open after «Отмена».
 */
function Item({ onSelect, children, className }) {
    const select = useSelect();
    return (_jsx(DropdownMenu.Item, { onSelect: () => select(onSelect), className: className, children: children }));
}
/**
 * Danger item — the common tail of every row menu (pipelines, classification,
 * channels, templates, admins, tags, comments, …). Same select path as `Item`.
 */
function DangerItem({ onSelect, children }) {
    return (_jsx(Item, { onSelect: onSelect, className: "text-[var(--color-state-danger-fg)]", children: children }));
}
RowActionsMenu.Item = Item;
RowActionsMenu.DangerItem = DangerItem;
//# sourceMappingURL=row-actions-menu.js.map